import React, { useRef, useState } from 'react';
import { UploadCloud, CheckCircle2, XCircle, Loader2, FileCheck, AlertTriangle } from 'lucide-react';
import { Card } from '../components/ui/Card';
import {
  extractOnboardingDoc,
  OnboardingDocType,
  OnboardingExtractionResult,
} from '../services/resortAdminApi';

interface FieldDef {
  key: string;
  label: string;
  validationKey?: string; // key in result.validation to render a badge for
}

interface SectionConfig {
  docType: OnboardingDocType;
  title: string;
  subtitle: string;
  fields: FieldDef[];
}

const KBIS: SectionConfig = {
  docType: 'kbis',
  title: 'Kbis / SIRENE — Business registration',
  subtitle: 'Extract: legal name, SIREN/SIRET, legal form, registered address, legal representative.',
  fields: [
    { key: 'legal_name', label: 'Legal name' },
    { key: 'siren', label: 'SIREN', validationKey: 'siren_valid' },
    { key: 'siret', label: 'SIRET', validationKey: 'siret_valid' },
    { key: 'legal_form', label: 'Legal form' },
    { key: 'registered_address', label: 'Registered address' },
    { key: 'legal_representative', label: 'Legal representative' },
  ],
};

const RIB: SectionConfig = {
  docType: 'rib',
  title: 'RIB — Banking details',
  subtitle: 'Extract: account holder, bank name, IBAN, BIC/SWIFT.',
  fields: [
    { key: 'account_holder', label: 'Account holder' },
    { key: 'bank_name', label: 'Bank name' },
    { key: 'iban', label: 'IBAN', validationKey: 'iban_valid' },
    { key: 'bic', label: 'BIC / SWIFT', validationKey: 'bic_valid' },
  ],
};

function stripHtml(s: string): string {
  return s.replace(/<[^>]*>/g, '').trim();
}

function parseFrappeError(err: any): string {
  const data = err?.response?.data;
  if (data?._server_messages) {
    try {
      const arr = JSON.parse(data._server_messages);
      const first = JSON.parse(arr[0]);
      if (first?.message) return stripHtml(String(first.message));
    } catch {
      /* fall through */
    }
  }
  if (data?.exception) return stripHtml(String(data.exception));
  if (data?.message) return stripHtml(String(data.message));
  return err?.message || 'Extraction failed';
}

const ValidationBadge: React.FC<{ valid: boolean | null | undefined }> = ({ valid }) => {
  if (valid === null || valid === undefined) return null;
  return valid ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 text-green-700 text-xs font-medium px-2 py-0.5">
      <CheckCircle2 size={12} /> valid
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 text-xs font-medium px-2 py-0.5">
      <XCircle size={12} /> invalid
    </span>
  );
};

const DocSection: React.FC<{ config: SectionConfig }> = ({ config }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<OnboardingExtractionResult | null>(null);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setFileName(file.name);
    setError(null);
    setResult(null);
    setLoading(true);
    try {
      const res = await extractOnboardingDoc(file, config.docType);
      setResult(res);
    } catch (err) {
      setError(parseFrappeError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card title={config.title}>
      <p className="text-sm text-gray-500 -mt-1 mb-4">{config.subtitle}</p>

      {/* Upload control */}
      <div className="flex items-center gap-3 flex-wrap">
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,image/png,image/jpeg,application/pdf"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg bg-alps-primary text-white px-4 py-2 text-sm font-medium hover:bg-alps-primary-dark transition-colors disabled:opacity-60"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
          {loading ? 'Extracting…' : result ? 'Upload another' : 'Upload document'}
        </button>
        {fileName && <span className="text-sm text-gray-600 truncate max-w-xs">{fileName}</span>}
        <span className="text-xs text-gray-400">PDF, PNG or JPG</span>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Extracted fields */}
      {result && (
        <div className="mt-5">
          <dl className="divide-y divide-gray-100">
            {config.fields.map((f) => {
              const value = result.fields?.[f.key] ?? null;
              const validity = f.validationKey ? result.validation?.[f.validationKey] : undefined;
              return (
                <div key={f.key} className="grid grid-cols-3 gap-4 py-2.5 items-center">
                  <dt className="text-sm font-medium text-gray-500">{f.label}</dt>
                  <dd className="col-span-2 text-sm text-gray-900 flex items-center gap-2">
                    <span className={value ? '' : 'text-gray-400'}>{value || '—'}</span>
                    {value ? <ValidationBadge valid={validity} /> : null}
                  </dd>
                </div>
              );
            })}
          </dl>

          <div className="mt-3 text-xs text-gray-400">
            Extraction mode: <span className="font-mono">{result.extraction_mode}</span> · Model:{' '}
            <span className="font-mono">{result.model}</span>
          </div>

          {/* Raw JSON for review */}
          <details className="mt-4 group">
            <summary className="cursor-pointer text-sm font-medium text-alps-primary hover:underline">
              View raw JSON
            </summary>
            <pre className="mt-2 max-h-96 overflow-auto rounded-lg bg-gray-900 text-gray-100 text-xs p-4">
              {JSON.stringify(result, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </Card>
  );
};

export const PartnerOnboarding: React.FC = () => {
  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <FileCheck className="text-alps-primary" size={28} />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Onboarding Docs — Docs check</h1>
          <p className="text-sm text-gray-500">
            Upload a merchant's registration documents to auto-extract their details for review.
            Nothing is saved to the merchant profile yet — this step is for review only.
          </p>
        </div>
      </div>

      <DocSection config={KBIS} />
      <DocSection config={RIB} />
    </div>
  );
};
